import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ChatWidget } from "@/components/ChatWidget";
import { LegalFooter } from "@/components/LegalFooter";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { SchoenBrand } from "@/components/SchoenBrand";
import { ShoppingBag, ArrowUpRight, ShieldCheck, Truck, Award } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const chf = (n) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n || 0);

export default function ShopPage() {
  const { lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const activeCategory = searchParams.get("category") || "all";
  const setActiveCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (!slug || slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next, { replace: true });
  };
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    shopApi.get(`/shop/categories?lang=${lang}`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    const catQ = activeCategory && activeCategory !== "all" ? `&category=${activeCategory}` : "";
    shopApi
      .get(`/shop/products?lang=${lang}&limit=200${catQ}`)
      .then((r) => setProducts(r.data.products || []))
      .finally(() => setLoading(false));
  }, [lang, activeCategory]);

  const totalCount = useMemo(
    () => categories.reduce((acc, c) => acc + (c.count || 0), 0),
    [categories]
  );

  return (
    <div className="min-h-screen ss-page" data-testid="shop-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[color:var(--ss-bg)]/80 backdrop-blur-xl border-b border-[color:var(--ss-border)]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/shop" data-testid="shop-brand" className="no-underline">
            <SchoenBrand size={42} showWordmark showTagline />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/shop/ebooks" data-testid="shop-nav-ebooks" className="ss-btn ss-btn-ghost text-xs">
              {t(lang, "shop_ebooks_link")}
            </Link>
            <LanguageSwitcher variant="ss" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12">
          <div className="max-w-3xl">
            <div className="ss-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--ss-gold)] mb-4">
              Swiss curation · Home · Living
            </div>
            <h1 className="ss-serif font-normal text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[color:var(--ss-ink)]"
                data-testid="shop-hero-headline">
              {t(lang, "shop_title")}
            </h1>
            <p className="mt-5 text-[color:var(--ss-ink-muted)] text-base sm:text-lg max-w-xl leading-relaxed">
              {t(lang, "shop_subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="shop-trust-badges">
              <span className="ss-badge"><ShieldCheck size={11} /> {t(lang, "shop_trust_secure")}</span>
              <span className="ss-badge"><Truck size={11} /> {t(lang, "shop_trust_shipping")}</span>
              <span className="ss-badge"><Award size={11} /> {t(lang, "shop_trust_curated")}</span>
            </div>
          </div>
        </div>
        <div className="ss-divider" />
      </section>

      {/* Category filter */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-10" data-testid="shop-category-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryChip
              slug="all"
              label={t(lang, "shop_cat_all") || "Tudo"}
              count={totalCount}
              active={activeCategory === "all"}
              onClick={() => setActiveCategory("all")}
            />
            {categories.map((c) => (
              <CategoryChip
                key={c.slug}
                slug={c.slug}
                label={c.label}
                count={c.count}
                active={activeCategory === c.slug}
                onClick={() => setActiveCategory(c.slug)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Product grid */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
          <h2 className="ss-serif text-2xl text-[color:var(--ss-ink)]">
            {t(lang, "shop_catalog_title")}
          </h2>
          <div className="ss-mono text-[10px] uppercase tracking-widest text-[color:var(--ss-ink-muted)]">
            {products.length} {t(lang, "shop_pieces_curated")}
          </div>
        </div>

        {loading && (
          <div className="text-center py-16 ss-mono text-xs text-[color:var(--ss-ink-muted)]">
            {t(lang, "shop_loading")}
          </div>
        )}
        {!loading && products.length === 0 && (
          <div className="text-center py-16 ss-mono text-xs text-[color:var(--ss-ink-muted)]">
            {t(lang, "shop_empty")}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => (
            <Link
              key={p.shopify_id}
              to={`/shop/${p.handle}`}
              className="ss-card overflow-hidden group no-underline"
              data-testid={`shop-product-${p.shopify_id}`}
            >
              <div className="aspect-square bg-[color:var(--ss-bg-soft)] overflow-hidden">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag size={40} className="text-[color:var(--ss-ink-muted)]/40" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="ss-serif text-lg leading-tight text-[color:var(--ss-ink)] ss-clamp-2 min-h-[3rem]">
                  {p.title}
                </h3>
                <p className="text-[color:var(--ss-ink-muted)] text-xs mt-2 ss-clamp-2">
                  {p.short_description}
                </p>
                <div className="mt-4 flex items-baseline justify-between">
                  <div className="ss-serif text-xl text-[color:var(--ss-ink)]">{chf(p.price_chf)}</div>
                  <div className="text-[color:var(--ss-gold)] group-hover:text-[color:var(--ss-ink)] transition-colors flex items-center gap-1 text-xs ss-mono uppercase tracking-widest">
                    {t(lang, "shop_view")} <ArrowUpRight size={12} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[color:var(--ss-bg-soft)] border-t border-[color:var(--ss-border)]">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <NewsletterSignup source="shop" />
        </div>
      </section>

      <LegalFooter />
      <ChatWidget />
    </div>
  );
}

function CategoryChip({ slug, label, count, active, onClick }) {
  return (
    <button
      data-testid={`cat-chip-${slug}`}
      onClick={onClick}
      className={`ss-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-all ${
        active
          ? "bg-[color:var(--ss-ink)] text-[color:var(--ss-bg)] border-[color:var(--ss-ink)]"
          : "bg-transparent text-[color:var(--ss-ink)] border-[color:var(--ss-border)] hover:border-[color:var(--ss-gold)]"
      }`}
    >
      {label} <span className="opacity-60 ml-1">· {count}</span>
    </button>
  );
}
